import React from 'react';
import { UserPlus, X, Mail, Briefcase, User, Image as ImageIcon, Loader2 } from 'lucide-react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../../../firebase';

const storage = getStorage();

const TeamSection = ({ formData, onInputChange }) => {
  const [uploadingIndex, setUploadingIndex] = React.useState(null);

  const handleAddMember = () => {
    const newMember = {
      firstName: '',
      lastName: '',
      role: '',
      bio: '',
      initials: '',
      email: '',
      avatarUrl: ''
    };
    onInputChange('teamMembers', 'teamMembers', [...formData.teamMembers, newMember]);
  };

  const handleRemoveMember = (index) => {
    const updatedMembers = formData.teamMembers.filter((_, i) => i !== index);
    onInputChange('teamMembers', 'teamMembers', updatedMembers);
  };

  const handleMemberChange = (index, field, value) => {
    onInputChange('teamMembers', index, { [field]: value });
  };

  const generateInitials = (first, last) => {
    return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase();
  };

  const handleAvatarUpload = async (index, file) => {
    if (!file) return;
    setUploadingIndex(index);
    try {
      const ext = file.name.split('.').pop();
      const storageRef = ref(storage, `avatars/${Date.now()}_${index}.${ext}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      handleMemberChange(index, 'avatarUrl', url);
    } catch (err) {
      alert('Failed to upload avatar.');
    } finally {
      setUploadingIndex(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">Team Members</h2>
        <button
          type="button"
          onClick={handleAddMember}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Add Team Member
        </button>
      </div>

      <div className="space-y-6">
        {formData.teamMembers.map((member, index) => (
          <div
            key={index}
            className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 relative"
          >
            <button
              type="button"
              onClick={() => handleRemoveMember(index)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex flex-col items-center mb-6">
              <div className="relative w-16 h-16 mb-2">
                {member.avatarUrl ? (
                  <img
                    src={member.avatarUrl}
                    alt={member.firstName || member.lastName || 'Avatar'}
                    className="w-16 h-16 rounded-full object-cover border-2 border-blue-400 shadow"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {member.initials || <User className="w-8 h-8" />}
                  </div>
                )}
                <label className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow cursor-pointer border border-gray-200">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => handleAvatarUpload(index, e.target.files[0])}
                    disabled={uploadingIndex === index}
                  />
                  {uploadingIndex === index ? (
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-blue-500" />
                  )}
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={member.firstName || ''}
                    onChange={e => {
                      handleMemberChange(index, 'firstName', e.target.value);
                      handleMemberChange(index, 'initials', generateInitials(e.target.value, member.lastName));
                    }}
                    className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                    placeholder="First name"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={member.lastName || ''}
                    onChange={e => {
                      handleMemberChange(index, 'lastName', e.target.value);
                      handleMemberChange(index, 'initials', generateInitials(member.firstName, e.target.value));
                    }}
                    className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                    placeholder="Last name"
                    autoComplete="off"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Briefcase className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={member.role}
                    onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                    className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                    placeholder="e.g., CEO, CTO"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={member.email}
                    onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
                    className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                    placeholder="email@company.com"
                  />
                </div>
              </div>

              {/* LinkedIn Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700">LinkedIn</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {/* Use LinkedIn SVG icon */}
                    <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5v-14c0-2.761-2.239-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm13.5 11.268h-3v-5.604c0-1.337-.025-3.063-1.868-3.063-1.868 0-2.154 1.459-2.154 2.967v5.7h-3v-10h2.881v1.367h.041c.401-.761 1.381-1.563 2.841-1.563 3.039 0 3.6 2.001 3.6 4.601v5.595z"/></svg>
                  </div>
                  <input
                    type="url"
                    value={member.linkedin || ''}
                    onChange={(e) => handleMemberChange(index, 'linkedin', e.target.value)}
                    className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                    placeholder="LinkedIn profile URL"
                  />
                </div>
              </div>

              {/* Website Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Website</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {/* Use Globe SVG icon */}
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/></svg>
                  </div>
                  <input
                    type="url"
                    value={member.website || ''}
                    onChange={(e) => handleMemberChange(index, 'website', e.target.value)}
                    className="block w-full pl-10 rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                    placeholder="Personal or company website"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Bio</label>
                <textarea
                  value={member.bio}
                  onChange={(e) => handleMemberChange(index, 'bio', e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white/50"
                  placeholder="Brief professional background"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 bg-blue-50 rounded-xl">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Tips for Team Section</h3>
        <ul className="text-sm text-blue-700 space-y-2">
          <li>• Include key team members with relevant experience</li>
          <li>• Highlight previous successful ventures or industry expertise</li>
          <li>• Keep bios concise but impactful</li>
          <li>• Ensure all contact information is current and professional</li>
        </ul>
      </div>
    </div>
  );
};

export default TeamSection;